import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import type { SequenceRunListItem } from "@unbox/api";
import { ArrowLeft, Dataflow03, FilterLines, SearchMd, Table as TableIcon, XClose } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePagination } from "@/api/pagination";
import {
  useBulkCancelSequenceRuns,
  useSequenceRunRowsFetcher,
  useSequenceRuns,
  useSequenceRunsCount,
  useSequences,
} from "@/api/sequences";
import { PaginationPageMinimalCenter } from "@/components/application/pagination/pagination";
import { SelectAllPagesButton } from "@/components/application/table/select-all-pages-button";
import { Table, TableCard } from "@/components/application/table/table";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { FilterPills } from "@/components/filters/filter-pills";
import { SequenceRunFilterPanel } from "@/components/filters/panels/sequence-run-filter-panel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { generateFilterSyncConfig, useAutoFilterUrlSync } from "@/hooks/use-auto-filter-sync";
import { useCrossPageSelection } from "@/hooks/use-cross-page-selection";
import { useClearableDebounce } from "@/hooks/use-debounce";
import { useSearchUrlSync } from "@/hooks/use-search-url-sync";
import { useSelectAllAcrossPages } from "@/hooks/use-select-all-across-pages";
import { useSort } from "@/hooks/use-sort";
import { useUrlFilterSync } from "@/hooks/use-url-filter-sync";
import { SequenceRunDialog } from "@/pages/sequences/sequence-run-dialog";
import { FiltersProvider, useFilters } from "@/providers/filter-provider";
import type { FilterField } from "@/providers/filter-registry";
import { useMerchant } from "@/providers/MerchantProvider";
import type { sequenceRunsRoute } from "@/router";
import { cx } from "@/utils/cx";
import { formatDate } from "@/utils/date-conversion";
import { formatCreatorId } from "@/utils/formatters";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { pluralize } from "@/utils/text";
import { toTitleCase } from "@/utils/toTitleCase";

const STATUS_COLORS: Record<string, "success" | "error" | "warning" | "gray"> = {
  active: "warning",
  completed: "success",
  cancelled: "gray",
  failed: "error",
};

const ACTIVE_FIELDS: FilterField[] = ["sequenceRunSequence", "sequenceRunStatus"];

export function SequenceRuns() {
  return (
    <FiltersProvider activeFields={ACTIVE_FIELDS}>
      <SequenceRunsContent />
    </FiltersProvider>
  );
}

function SequenceRunsContent() {
  const merchantId = useMerchant();
  const navigate = useNavigate() as ReturnType<(typeof sequenceRunsRoute)["useNavigate"]>;
  const urlParams = useSearch({ strict: false }) as ReturnType<(typeof sequenceRunsRoute)["useSearch"]>;
  const { search: urlSearch } = urlParams;

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [search, setSearch] = useState(urlSearch || "");
  const { value: debouncedSearch, clear: clearSearch } = useClearableDebounce(search);

  const { data: sequences = [] } = useSequences();
  const { form, apiFilters, reset, activeFields, hasActiveFilters } = useFilters();
  const { sort, descriptor, setFromDescriptor } = useSort<"username" | "sequenceName" | "status" | "createdAt">(
    "createdAt",
  );

  // Sync filters with URL — exclude sequenceRunSequence from generic sync (URL stores ID only, form stores {id, label})
  const urlSyncFields = useMemo(() => activeFields.filter((f) => f !== "sequenceRunSequence"), [activeFields]);
  useAutoFilterUrlSync(form, urlParams, urlSyncFields);
  const { fieldMapping, currentSearch } = useMemo(
    () => generateFilterSyncConfig(urlSyncFields, urlParams),
    [urlSyncFields, urlParams],
  );
  useUrlFilterSync({ form, fieldMapping, navigate, currentSearch });
  useSearchUrlSync({ search, setSearch, navigate, currentSearch: { search: urlSearch } });

  // Manual bidirectional sync for sequenceRunSequence: URL (string ID) ↔ form ({id, label})
  const isSequenceSyncInit = useRef(true);
  useEffect(() => {
    const urlId = urlParams.sequenceRunSequence;
    const formValue = form.getValues("sequenceRunSequence");
    if (urlId) {
      if (formValue?.id !== urlId) {
        const seq = sequences.find((s) => s.id === urlId);
        if (seq) form.setValue("sequenceRunSequence", { id: seq.id, label: seq.name });
      }
    } else if (formValue) {
      form.resetField("sequenceRunSequence");
    }
  }, [urlParams.sequenceRunSequence, sequences, form]);

  useEffect(() => {
    if (isSequenceSyncInit.current) {
      isSequenceSyncInit.current = false;
      return;
    }
    const formValue = form.getValues("sequenceRunSequence");
    const formId = formValue?.id;
    const urlId = urlParams.sequenceRunSequence;
    if (formId !== urlId) {
      navigate({
        search: (prev: ReturnType<(typeof sequenceRunsRoute)["useSearch"]>) => ({
          ...prev,
          sequenceRunSequence: formId,
        }),
        replace: true,
      });
    }
  }, [form.watch("sequenceRunSequence"), navigate, urlParams.sequenceRunSequence]);

  const sequenceId = apiFilters.sequenceRunSequenceId;
  const status = apiFilters.sequenceRunStatus;

  const pagination = usePagination();
  const runsQuery = useMemo(
    () => ({
      sequenceId,
      status,
      search: debouncedSearch || undefined,
      not: apiFilters.not,
      sort: sort.column ? sort : undefined,
    }),
    [sequenceId, status, debouncedSearch, apiFilters.not, sort],
  );
  const { data: runs = [], isLoading } = useSequenceRuns({ ...runsQuery, pagination: pagination.pagination });
  const { data: total = 0 } = useSequenceRunsCount({
    sequenceId,
    status,
    search: debouncedSearch || undefined,
    not: apiFilters.not,
  });

  const {
    selectedKeys: selected,
    selectedData,
    isAllPagesSelected,
    selectedCount,
    clear: clearSelection,
    selectAllAcrossPages,
    createSelectionHandler,
  } = useCrossPageSelection<SequenceRunListItem, { id: string; status: string }>({
    getKey: useCallback((run: SequenceRunListItem) => run.id, []),
    toData: useCallback((run: SequenceRunListItem) => ({ id: run.id, status: run.status }), []),
  });
  const handleSelectionChange = useMemo(() => createSelectionHandler(runs), [createSelectionHandler, runs]);
  const bulkCancel = useBulkCancelSequenceRuns();

  const fetchChunk = useSequenceRunRowsFetcher(runsQuery);
  const selectAll = useSelectAllAcrossPages({
    fetchChunk,
    total,
    loadedCount: runs.length,
    selection: { selectedCount, isAllPagesSelected, selectAllAcrossPages, clear: clearSelection },
    resetKey: JSON.stringify(runsQuery),
  });

  const activeSelectedCount = useMemo(
    () => [...selectedData.values()].filter((r) => r.status === "active").length,
    [selectedData],
  );

  const totalPages = Math.ceil(total / pagination.pagination.limit);

  const resetFilters = () => {
    setSearch("");
    clearSearch();
    reset();
  };

  return (
    <div className="flex flex-col w-full h-screen gap-8 items-center">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col gap-5 h-full px-16 pt-16 pb-8`}>
        <div className="flex flex-row h-full w-full">
          <div className={cx("flex flex-col gap-5 w-full h-full transition-all duration-300", filtersOpen && "mr-96")}>
            {/* Header */}
            <div className="flex flex-col">
              <Link
                to="/merchant/$merchantId/automate-outreach"
                params={{ merchantId }}
                className="flex flex-row items-center gap-1.5 text-sm text-tertiary hover:text-secondary mb-2 w-fit"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Automate Outreach
              </Link>
              <h1 className="text-xl font-normal text-secondary flex flex-row items-center gap-2 mb-1">
                <Dataflow03 className="w-5 h-5" strokeWidth={1.8} />
                Sequence Runs
              </h1>
              <span className="text-quaternary text-sm">View active and completed sequence runs</span>
            </div>

            <div className="flex-1 flex flex-col min-h-0">
              {/* Filters row */}
              <div className="relative flex flex-shrink-0 flex-row items-center justify-between gap-4 mb-4">
                {selected.length > 0 && (
                  <div className="absolute inset-0 z-20 flex flex-row justify-between items-center rounded-lg bg-brand-primary -my-2 p-2">
                    <div className="flex flex-row items-center gap-2">
                      <button
                        type="button"
                        onClick={selectAll.clear}
                        className="p-1 rounded hover:bg-white/10 transition-colors"
                      >
                        <XClose className="w-4 h-4" />
                      </button>
                      <span className="text-sm font-medium">{selectedCount.toLocaleString()} Selected</span>
                      <SelectAllPagesButton state={selectAll} />
                    </div>
                    <Button
                      color="primary-destructive"
                      size="sm"
                      isDisabled={activeSelectedCount === 0 || selectAll.isFetching}
                      onClick={() => setConfirmCancelOpen(true)}
                    >
                      Cancel Runs
                    </Button>
                  </div>
                )}
                <span className="text-sm font-semibold text-gray-500">{pluralize(total, "Run", "Runs")}</span>
                <div className="flex items-center gap-2">
                  <Input
                    size="sm"
                    inputClassName="text-sm"
                    className="w-72"
                    value={search}
                    onChange={setSearch}
                    onClear={() => {
                      setSearch("");
                      clearSearch();
                    }}
                    placeholder="Search Creator Name..."
                    icon={SearchMd}
                  />
                  <Button
                    className="font-medium text-xs"
                    color="secondary"
                    size="sm"
                    onClick={() => setFiltersOpen((prev) => !prev)}
                    iconLeading={<FilterLines className="w-4 h-4" />}
                  >
                    Filter
                  </Button>
                </div>
              </div>

              {(hasActiveFilters || !!debouncedSearch) && <FilterPills className="pb-4" />}

              {/* Table */}
              <div className="flex flex-col w-full flex-1 min-h-0">
                <TableCard.Root className="bg-secondary_alt flex flex-col w-full flex-1" size="sm" rounded={false}>
                  <Table
                    aria-label="Sequence Runs"
                    selectionMode="multiple"
                    selectedKeys={selected}
                    onSelectionChange={handleSelectionChange}
                    sortDescriptor={descriptor}
                    onSortChange={setFromDescriptor}
                    className={cx(runs.length === 0 && "h-full")}
                  >
                    <Table.Header className="sticky top-0 z-10 bg-secondary" allowSelectAll>
                      <Table.Head id="username" label="Creator" className="px-4 w-1/4" isRowHeader allowsSorting />
                      <Table.Head id="sequenceName" label="Sequence" className="px-4 w-1/4" allowsSorting />
                      <Table.Head id="progress" label="Progress" className="px-4 w-1/6" />
                      <Table.Head id="status" label="Status" className="px-4 w-1/6" allowsSorting />
                      <Table.Head id="createdAt" label="Started" className="px-4 w-1/6" allowsSorting />
                    </Table.Header>

                    <Table.Body
                      className="text-xs bg-secondary_alt"
                      items={runs}
                      renderEmptyState={() => (
                        <UnboxTableEmptyStateContent
                          rows={isLoading ? undefined : []}
                          emptyState={{ icon: TableIcon, title: "No runs found" }}
                        />
                      )}
                    >
                      {(run) => (
                        <Table.Row
                          key={run.id}
                          className="bg-primary border-b cursor-pointer hover:bg-secondary"
                          onAction={() => setSelectedRunId(run.id)}
                        >
                          <Table.Cell className="px-4">
                            <span className="text-sm font-medium text-primary">
                              {formatCreatorId(run.username ?? run.creatorId ?? "Unknown")}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="px-4">
                            <Link
                              to="/merchant/$merchantId/automate-outreach/$sequenceId"
                              params={{ merchantId, sequenceId: run.sequenceId }}
                              className="text-sm font-medium text-brand-700 hover:underline"
                            >
                              {run.sequenceName}
                            </Link>
                          </Table.Cell>
                          <Table.Cell className="px-4">
                            <span className="text-sm text-secondary">
                              {run.completedSteps}/{run.totalSteps}
                            </span>
                          </Table.Cell>
                          <Table.Cell className="px-4">
                            <Badge size="sm" color={STATUS_COLORS[run.status] ?? "gray"}>
                              {toTitleCase(run.status)}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell className="px-4">
                            <span className="text-sm text-tertiary">{formatDate(run.createdAt)}</span>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </TableCard.Root>

                <PaginationPageMinimalCenter
                  page={pagination.page}
                  total={totalPages}
                  onPageChange={pagination.setPage}
                  className="py-3 md:px-6 md:pt-2 md:pb-2 bg-primary"
                />
              </div>
            </div>
          </div>

          <SequenceRunFilterPanel open={filtersOpen} close={() => setFiltersOpen(false)} sequences={sequences} />
        </div>
      </div>
      <SequenceRunDialog runId={selectedRunId} onOpenChange={(open) => !open && setSelectedRunId(null)} />
      <Dialog open={confirmCancelOpen} onOpenChange={setConfirmCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Sequence Runs</DialogTitle>
            <DialogDescription>
              {activeSelectedCount === 0
                ? "None of the selected runs are active."
                : `${pluralize(activeSelectedCount, "active run", "active runs")} will be cancelled.${selected.length - activeSelectedCount > 0 ? ` ${pluralize(selected.length - activeSelectedCount, "non-active run", "non-active runs")} will be skipped.` : ""}`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button color="secondary" size="sm" onClick={() => setConfirmCancelOpen(false)}>
              Go Back
            </Button>
            <Button
              color="primary-destructive"
              size="sm"
              isDisabled={activeSelectedCount === 0}
              isLoading={bulkCancel.isPending}
              onClick={async () => {
                const activeRunIds = [...selectedData.values()].filter((r) => r.status === "active").map((r) => r.id);
                await bulkCancel.mutateAsync(activeRunIds);
                setConfirmCancelOpen(false);
                selectAll.clear();
              }}
            >
              Cancel {pluralize(activeSelectedCount, "Run", "Runs")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
